/**
 * pdf.js 的加载与最小接口面（文本提取与页面渲染共用）。
 *
 * 为什么单独一支：pdf.js 体积大（含 1.2MB worker），必须**动态加载**；
 * 同时它自带 Worker，因此引擎声明 `runtime: 'main-thread'` —— 主线程只负责调度，
 * 真正的解析在 pdf.js 自己的 Worker 里，UI 不会被阻塞。
 */

export interface PdfTextItem {
  str?: string;
  hasEOL?: boolean;
  transform?: number[];
  width?: number;
}

export interface PdfRenderTask {
  promise: Promise<void>;
  cancel?(): void;
}

export interface PdfViewport {
  width: number;
  height: number;
}

export interface PdfPageLike {
  getTextContent(): Promise<{ items: PdfTextItem[] }>;
  getViewport(params: { scale: number }): PdfViewport;
  render(params: { canvas?: unknown; canvasContext: unknown; viewport: PdfViewport }): PdfRenderTask;
}

export interface PdfDocumentLike {
  numPages: number;
  getPage(pageNumber: number): Promise<PdfPageLike>;
  destroy?(): void | Promise<void>;
}

export interface PdfJsLike {
  getDocument(params: { data: Uint8Array; isEvalSupported?: boolean; useSystemFonts?: boolean }): {
    promise: Promise<PdfDocumentLike>;
  };
  GlobalWorkerOptions?: { workerSrc?: string };
}

export type PdfJsLoader = () => Promise<PdfJsLike>;

export async function defaultLoadPdfjs(): Promise<PdfJsLike> {
  const pdfjs = (await import('pdfjs-dist')) as unknown as PdfJsLike;
  // pdf.js 必须拿到独立的 Worker 脚本地址：`?url` 让 Vite 把它产出为静态资源。
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  if (pdfjs.GlobalWorkerOptions) pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return pdfjs;
}
